import React, { useMemo, useState } from "react";

import FormInput from "../../Inputs/FormInput";
import { formatDateIST } from "../../../utils/dateFormatter";
import DocumentConsumptionGrid from "./DocumentConsumptionGrid";

import styles from "./pageModal.module.css";

const StepHeader = ({ currentStep }) => {
  const steps = ["Search Quotation", "Select Product", "Enter PO"];
  return (
    <div className={styles.poStepper}>
      {steps.map((label, index) => {
        const stepNo = index + 1;
        const active = currentStep === stepNo;
        return (
          <div key={label} className={`${styles.poStepArrow} ${active ? styles.poStepArrowActive : ""}`}>
            {label}
          </div>
        );
      })}
    </div>
  );
};

const PurchaseOrderModalForm = ({
  formData,
  errors,
  readOnly,
  quotationCatalog,
  selectedQuotation,
  selectedQuotationIds,
  onToggleQuotationSelection,
  poItems,
  handlePoItemChange,
  step,
  setStep,
  handleChange,
}) => {
  const [search, setSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const filteredQuotations = useMemo(() => {
    const query = String(search || "").toLowerCase().trim();
    if (!query) return quotationCatalog;
    return quotationCatalog.filter((row) =>
      `${row.quotation_code} ${row.customer_name} ${row.purchase_invoice || ""}`
        .toLowerCase()
        .includes(query),
    );
  }, [quotationCatalog, search]);

  const filteredPoItems = useMemo(() => {
    const query = String(productSearch || "").toLowerCase().trim();
    if (!query) return poItems;
    return poItems.filter((row) =>
      `${row.product_name || ""}`.toLowerCase().includes(query),
    );
  }, [poItems, productSearch]);

  const groupedPoItems = useMemo(() => {
    const quotationCodeById = (quotationCatalog || []).reduce((acc, row) => {
      acc[String(row.quotation_id)] = row.quotation_code;
      return acc;
    }, {});

    const groups = [];
    const groupByQuotationId = {};

    filteredPoItems.forEach((row) => {
      const quotationId = String(row.quotation_fid || row.quotation_code || "unknown");
      if (!groupByQuotationId[quotationId]) {
        groupByQuotationId[quotationId] = {
          quotationId,
          quotationCode:
            row.quotation_code || quotationCodeById[String(row.quotation_fid || "")] || quotationId,
          items: [],
        };
        groups.push(groupByQuotationId[quotationId]);
      }
      groupByQuotationId[quotationId].items.push(row);
    });

    return groups;
  }, [filteredPoItems, quotationCatalog]);

  const getQuotationVolume = (quotationRow = {}) =>
    (quotationRow.items || []).reduce((sum, item) => sum + Number(item.volume || 0), 0);

  const handleSelectQuotation = (quotation) => {
    const quotationId = String(quotation?.quotation_id || "");
    if (!quotationId) return;
    onToggleQuotationSelection(quotation);
    setProductSearch("");
  };

  return (
    <div className={styles.poFlowWrap}>
      <div className={styles.poHeadWrap}>
        <h3 className={styles.poTitle}>Purchase Order Management</h3>
        <div className={styles.poSubTitle}>
          {step === 1 ? "(Step 1) Search Quotation" : step === 2 ? "(Step 2) Select Product" : "(Step 3) Enter PO"}
        </div>
      </div>

      <StepHeader currentStep={step} />

      {step === 1 && (
        <div className={styles.poStepBlock}>
          <div className={styles.poSearchRow}>
            <div className={styles.poSearchLabel}>Search Quotation</div>
            <div className={styles.poSearchInputWrap}>
              <FormInput
                type="text"
                value={search}
                disabled={readOnly}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>

          <div className={styles.poTableWrap}>
            <table className={styles.poTable}>
              <thead>
                <tr>
                  <th>Quotation ID</th>
                  <th>Quotation PI/PO</th>
                  <th>Customer Name</th>
                  <th>Quotation Date</th>
                  <th>Volume</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotations.length ? (
                  filteredQuotations.map((row) => (
                    <tr
                      key={row.quotation_id}
                      className={
                        String(selectedQuotation?.quotation_id) === String(row.quotation_id)
                          ? styles.poSelectedRow
                          : ""
                      }
                    >
                      <td>{row.quotation_code}</td>
                      <td>{row.purchase_invoice || "-"}</td>
                      <td>{row.customer_name}</td>
                      <td>{formatDateIST(row.quotation_date)}</td>
                      <td>{getQuotationVolume(row)}</td>
                      <td>
                        <button
                          type="button"
                          className={`${styles.poSelectBtn} ${
                            (selectedQuotationIds || []).includes(String(row.quotation_id))
                              ? styles.poSelectBtnSelected
                              : styles.poSelectBtnDefault
                          }`}
                          onClick={() => handleSelectQuotation(row)}
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className={styles.poEmptyCell}>
                      No quotation found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {step === 2 && (
        <div className={styles.poStepBlock}>
          <div className={styles.poSearchRow}>
            <div className={styles.poSearchLabel}>Search Product</div>
            <div className={styles.poSearchInputWrap}>
              <FormInput
                type="text"
                value={productSearch}
                disabled={readOnly}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search Product"
              />
            </div>
          </div>
          {errors.po_items && <div className={styles.partRowError}>{errors.po_items}</div>}

          {groupedPoItems.length ? (
            groupedPoItems.map((group) => (
              <div key={`po-group-${group.quotationId}`} className={styles.poQuotationGroup}>
                <div className={styles.poQuotationTitle}>
                  <strong>Quotation ID :</strong> {group.quotationCode}
                </div>

                <DocumentConsumptionGrid
                  rows={group.items.map((row) => ({
                    ...row,
                    row_index: poItems.findIndex(
                      (item) => String(item.quotation_item_fid) === String(row.quotation_item_fid),
                    ),
                  }))}
                  errors={errors}
                  readOnly={readOnly}
                  onChangeQuantity={(rowIndex, value) => handlePoItemChange(rowIndex, "quantity", value)}
                  onChangeRate={(rowIndex, value) => handlePoItemChange(rowIndex, "rate", value)}
                />
              </div>
            ))
          ) : (
            <div className={styles.poEmptyCell}>No products found</div>
          )}

        </div>
      )}

      {step === 3 && (
        <div className={styles.poStepBlock}>
          <div className={styles.formGrid}>
            <FormInput
              label="Enter PO ID"
              type="text"
              value={formData.po_code || ""}
              error={errors.po_code}
              disabled={readOnly}
              onChange={(e) => handleChange("po_code", e.target.value)}
            />
          </div>

        </div>
      )}
    </div>
  );
};

export default PurchaseOrderModalForm;
